/**
 * US-158: CCPA & US Compliance Scanning Engine
 * Task: T-158-001
 *
 * Evaluates CCPA compliance state by calling backend endpoints:
 * - GET /api/texas/ccpa/snapshot -> current compliance snapshot
 * - GET /api/texas/ccpa/violations -> active violations
 * - POST /api/texas/ccpa/check -> run a compliance check
 * - GET /api/texas/ccpa/residency -> data residency status
 */

import { landlordApi } from '../../services/api';

export interface Violation {
  id: string;
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  detectedAt: string;
  deadline?: string;
}

export interface ResidencyStatus {
  inScope: boolean;
  region: string;
  compliant: boolean;
}

export interface ScanResult {
  id: string;
  timestamp: string;
  status: 'PASS' | 'FAIL' | 'WARN';
  violations: Violation[];
  residency: ResidencyStatus;
}

/**
 * Unwraps Axios response and standard backend envelope { success: true, data: T }.
 */
function unwrapResponse<T>(res: any): T {
  if (res && res.data !== undefined) {
    if (res.data && typeof res.data === 'object' && 'data' in res.data && 'success' in res.data) {
      return res.data.data;
    }
    return res.data;
  }
  return res;
}

/**
 * Retrieve active CCPA compliance violations.
 * GET /api/texas/ccpa/violations
 */
export async function getViolations(): Promise<Violation[]> {
  const res = await landlordApi.get('/api/texas/ccpa/violations');
  const data = unwrapResponse<any>(res);
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.violations)) {
    return data.violations;
  }
  return [];
}

/**
 * Retrieve data residency status.
 * GET /api/texas/ccpa/residency
 */
export async function getResidencyStatus(): Promise<ResidencyStatus> {
  const res = await landlordApi.get('/api/texas/ccpa/residency');
  const data = unwrapResponse<any>(res);
  return {
    inScope: data?.inScope ?? true,
    region: data?.region ?? 'US-TX',
    compliant: data?.compliant ?? true,
  };
}

/**
 * Retrieve current CCPA compliance snapshot.
 * GET /api/texas/ccpa/snapshot
 */
export async function getLatestSnapshot(): Promise<ScanResult | null> {
  const res = await landlordApi.get('/api/texas/ccpa/snapshot');
  const data = unwrapResponse<any>(res);
  if (!data) return null;

  return {
    id: data.id || `snap-${Date.now()}`,
    timestamp: data.timestamp || new Date().toISOString(),
    status: data.status || 'PASS',
    violations: Array.isArray(data.violations) ? data.violations : [],
    residency: data.residency || {
      inScope: true,
      region: 'US-TX',
      compliant: true,
    },
  };
}

/**
 * Execute an on-demand CCPA compliance check.
 * POST /api/texas/ccpa/check
 */
export async function runComplianceScan(): Promise<ScanResult> {
  let res: any;
  try {
    res = await landlordApi.post('/api/texas/ccpa/check');
  } catch (err: any) {
    // 422 is the documented response when the snapshot is NOT compliant — the
    // body is the snapshot itself, not an error. Treat it as a result so a
    // failing compliance check renders as FAIL instead of throwing.
    if (err?.response?.status === 422 && err.response.data) {
      res = err.response;
    } else {
      throw err;
    }
  }
  const data = unwrapResponse<any>(res) || {};

  let residency: ResidencyStatus = data.residency;
  let violations: Violation[] = data.violations;

  if (!residency) {
    try {
      residency = await getResidencyStatus();
    } catch {
      residency = { inScope: true, region: 'US-TX', compliant: true };
    }
  }

  if (!violations) {
    try {
      violations = await getViolations();
    } catch {
      violations = [];
    }
  }

  let status: 'PASS' | 'FAIL' | 'WARN' = data.status;
  if (!status) {
    const hasCriticalOrHigh = violations.some(
      (v: Violation) => v.severity === 'CRITICAL' || v.severity === 'HIGH'
    );
    if (data.compliant === false || !residency.compliant || hasCriticalOrHigh) {
      status = 'FAIL';
    } else if (violations.length > 0) {
      status = 'WARN';
    } else {
      status = 'PASS';
    }
  }

  return {
    id: data.id || `scan-${Date.now()}`,
    timestamp: data.timestamp || new Date().toISOString(),
    status,
    violations: Array.isArray(violations) ? violations : [],
    residency,
  };
}

export const complianceScannerEngine = {
  runComplianceScan,
  getLatestSnapshot,
  getViolations,
  getResidencyStatus,
};

export default complianceScannerEngine;
